/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { useQuery } from "@tanstack/react-query";
import { BellIcon } from "@heroicons/react/24/outline";
import { getAnomalies, getOverview } from "@/api/overview";
import { useNotificationCenter } from "@/context/notificationCenter";
import { cn } from "@/lib/utils";

/** The bell that opens the notification centre; its badge is the number of open problems (red when one is critical). */
export const NotificationBell: React.FC<{ className?: string }> = ({ className }) => {
  const { openCenter } = useNotificationCenter();
  const { data: overview } = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: 30_000 });
  const { data: anomalies = [] } = useQuery({ queryKey: ["anomalies"], queryFn: getAnomalies, refetchInterval: 30_000 });
  const count = (overview?.problems.length ?? 0) + anomalies.length;
  const crit = overview?.problems.some((p) => p.severity === "crit") ?? false;
  return (
    <button
      type="button"
      className={cn("nc-bell", className)}
      aria-label={count > 0 ? `Notifications, ${count} open` : "Notifications"}
      title="Notifications"
      onClick={() => openCenter({ tab: "problems" })}
    >
      <BellIcon className="h-5 w-5" />
      {count > 0 && <span className={cn("nc-badge", crit ? "nc-badge--crit" : "nc-badge--warn")}>{count > 99 ? "99+" : count}</span>}
    </button>
  );
};
